import * as THREE from "three";
import { EnvironmentElement } from "../../../shared/src/environment.types";
import { ENVIRONMENT_CONFIG } from "./config.ts";
import { GraphicsQuality } from "./config/types";
import { GraphicsSettingsManager, GRAPHICS_PRESETS } from "./config/graphics";

const PLAYERS_COUNT = 1;

const _grassMatrix = new THREE.Matrix4();
const _grassRotationY = new THREE.Matrix4();

// Konfiguracja poziomów szczegółowości dla trawy
interface GrassLODLevel {
  distance: number; // Maksymalna odległość dla tego poziomu
  density: number; // Mnożnik gęstości trawy (1.0 = pełna gęstość)
  animated: boolean; // Czy trawa na tym poziomie ma być animowana
}

const GRASS_LOD_LEVELS: Record<GraphicsQuality, GrassLODLevel[]> = {
  low: [{ distance: 20, density: 0.0, animated: true }],
  medium: [
    { distance: 30, density: 1.0, animated: true },
    { distance: 50, density: 0.7, animated: true },
    { distance: 60, density: 0.4, animated: true },
    { distance: 90, density: 0.1, animated: false },
  ],
  high: [
    { distance: 40, density: 1.0, animated: true },
    { distance: 60, density: 0.8, animated: true },
    { distance: 80, density: 0.6, animated: true },
    { distance: 100, density: 0.3, animated: true },
    { distance: 120, density: 0.1, animated: false },
  ],
};

/**
 * Klasa odpowiedzialna za renderowanie trawy w grze.
 * Wykorzystuje InstancedMesh dla wydajnego renderowania wielu instancji trawy.
 * Implementuje shadery dla realistycznego wyglądu i interakcji z czołgami.
 */
export class GrassRenderer {
  private scene: THREE.Scene;
  private grassPatches: Map<string, THREE.InstancedMesh> = new Map();
  private grassPatchesData: Map<
    string,
    { position: THREE.Vector3; scale: number; lastLODLevel?: GrassLODLevel; isCulled?: boolean }
  > = new Map();
  private groundPatches: Map<string, THREE.Mesh> = new Map(); // Nowa mapa dla podłoża
  private clock: THREE.Clock;
  private playerPositions: Map<string, THREE.Vector3> = new Map();
  private playerPositionBuffer: THREE.Vector3[] = Array.from(
    { length: PLAYERS_COUNT },
    () => new THREE.Vector3(0, -1000, 0),
  );
  private camera: THREE.Camera | null = null;
  private currentQuality: GraphicsQuality = "medium";
  private instanceScale: THREE.Vector3 = new THREE.Vector3();

  // Geometria dla trawy - współdzielona przez wszystkie instancje
  private grassGeometry: THREE.PlaneGeometry;
  private grassMaterial: THREE.ShaderMaterial;
  private groundMaterial: THREE.MeshStandardMaterial;
  // Perf: podłoże patchy trawy - jedna współdzielona geometria jednostkowa
  // skalowana per patch zamiast nowego CircleGeometry per patch.
  private static sharedGroundGeometry: THREE.CircleGeometry | null = null;

  private static getGroundGeometry(): THREE.CircleGeometry {
    if (!GrassRenderer.sharedGroundGeometry) {
      GrassRenderer.sharedGroundGeometry = new THREE.CircleGeometry(1, 16);
    }
    return GrassRenderer.sharedGroundGeometry;
  }
  //private textureLoader: THREE.TextureLoader;

  // Shadery dla trawy
  private vertexShader = `
        uniform float time;
        uniform vec3 playerPositions[1]; // Maksymalnie PLAYERS_COUNT graczy
        uniform int playerCount;
        uniform float playerRadius;
        
        attribute float grassHeight;
        
        varying float vDistanceFromGround;
        varying vec2 vUv;
        varying float vFogDepth;
        
        // Funkcja generująca losowe wartości
        float rand(float n) {
            return fract(sin(n) * 43758.5453123);
        }
        
        // Funkcja do generowania szumu
        float noise(vec2 n) {
            const vec2 d = vec2(0.0, 1.0);
            vec2 b = floor(n);
            vec2 f = smoothstep(vec2(0.0), vec2(1.0), fract(n));
            return mix(
                mix(rand(dot(b, vec2(127.1, 311.7))), rand(dot(b + d.yx, vec2(127.1, 311.7))), f.x),
                mix(rand(dot(b + d.xy, vec2(127.1, 311.7))), rand(dot(b + d.yy, vec2(127.1, 311.7))), f.x),
                f.y
            );
        }
        
        void main() {
            vUv = uv;
            
            // Pozycja w świecie
            vec4 worldPosition = modelMatrix * instanceMatrix * vec4(position, 1.0);
            vDistanceFromGround = position.y + 0.5;
            
            // Kołysanie trawy na wietrze - zmniejszona intensywność
            float instanceIndex = float(gl_InstanceID);
            float noiseValue = noise(vec2(instanceIndex * 0.05, time * 0.05)); // Zmniejszone częstotliwości
            
            // Zredukowana siła wiatru
            float windStrength = 0.05 + noiseValue * 0.02;
            
            // Wolniejsze częstotliwości dla bardziej naturalnego ruchu
            float windFreq1 = time * 0.2 + instanceIndex * 0.1;
            float windFreq2 = time * 0.15 + instanceIndex * 0.2;
            
            // Wiatr silniejszy na górze źdźbła, ale ogólnie słabszy
            vec3 wind = vec3(
                sin(windFreq1) * windStrength * vDistanceFromGround * noiseValue,
                0.0,
                cos(windFreq2) * windStrength * 0.3 * vDistanceFromGround * noiseValue
            );
            
            // Subtelniejszy ruch falowy
            float waveEffect = sin(worldPosition.x * 0.1 + time * 0.1) * cos(worldPosition.z * 0.1 + time * 0.05) * 0.02;
            wind.x += waveEffect * vDistanceFromGround;
            
            // Interakcja z graczami - zredukowana intensywność - ograniczenie do PLAYERS_COUNT gracza
            vec3 playerOffset = vec3(0.0);
            for (int i = 0; i < 1; i++) {
                if (i >= playerCount) break;
                
                vec3 playerPos = playerPositions[i];
                float dist = distance(worldPosition.xyz, playerPos);
                
                if (dist < playerRadius) {
                    vec3 direction = normalize(worldPosition.xyz - playerPos);
                    float strength = 1.0 - (dist / playerRadius);
                    
                    // Zmniejszone ugięcie dla bliższych źdźbeł
                    float bendFactor = pow(strength, 2.0) * 0.4;
                    playerOffset += direction * bendFactor * vDistanceFromGround;
                }
            }
            
            // Finalna pozycja z uwzględnieniem wiatru i interakcji
            worldPosition.xyz += wind + playerOffset;
            
            vec4 mvPosition = viewMatrix * worldPosition;
            gl_Position = projectionMatrix * mvPosition;
            
            // Obliczenie głębokości dla mgły
            vFogDepth = -mvPosition.z;
        }
    `;

  private fragmentShader = `
        uniform vec3 grassColor;
        uniform vec3 grassTipColor;
        uniform vec3 fogColor;
        uniform float fogNear;
        uniform float fogFar;
        
        varying float vDistanceFromGround;
        varying vec2 vUv;
        varying float vFogDepth;
        
        void main() {
            // Gradient koloru od podstawy do czubka trawy
            vec3 color = mix(grassColor, grassTipColor, vDistanceFromGround);
            
            // Dodanie cieniowania dla większej głębi
            float shadow = mix(0.7, 1.0, vDistanceFromGround);
            
            // Dodanie subtelnych wariacji koloru dla większego realizmu
            float colorVariation = fract(sin(vUv.x * 100.0) * 43758.5453123) * 0.1 - 0.05;
            color = color * (shadow + colorVariation);
            
            // Dodanie lekkiego podświetlenia na krawędziach dla efektu 3D
            float edgeLight = pow(1.0 - abs(vUv.x - 0.5) * 2.0, 2.0) * 0.15;
            color += edgeLight * grassTipColor * vDistanceFromGround;

            // Obliczenie wpływu mgły
            float fogFactor = smoothstep(fogNear, fogFar, vFogDepth);
            color = mix(color, fogColor, fogFactor);
            
            gl_FragColor = vec4(color, 1.0);
        }
    `;

  private lastLODUpdateTime: number = 0;
  private readonly LOD_UPDATE_INTERVAL: number = 500; // ms

  constructor(scene: THREE.Scene) {
    // Listen for graphics settings changes
    document.addEventListener("graphics:settingsChanged", ((event: CustomEvent) => {
      const settings = event.detail.settings;
      this.currentQuality = settings.quality;
      // Update all grass patches with new quality settings
      this.grassPatchesData.forEach((_, id) => this.updateGrassPatchLOD(id));
    }) as EventListener);
    this.scene = scene;
    this.clock = new THREE.Clock();
    //this.textureLoader = new THREE.TextureLoader();

    // Inicjalizacja geometrii dla trawy
    this.grassGeometry = new THREE.PlaneGeometry(1, 1, 1, 2); // Więcej segmentów dla lepszej animacji 2->4

    // Przesunięcie wierzchołków, aby trawa była bardziej trójwymiarowa
    const positionAttribute = this.grassGeometry.getAttribute("position");
    const positions = positionAttribute.array;

    // Dodajemy losowe przesunięcia do wierzchołków dla bardziej naturalnego wyglądu
    for (let i = 0; i < positions.length; i += 3) {
      // Tylko dla wierzchołków nie będących na dole (y > -0.5)
      if (positions[i + 1] > -0.5) {
        // Dodaj losowe przesunięcie w osi X
        positions[i] += (Math.random() - 0.5) * 0.1;
      }
    }

    // Aktualizujemy atrybuty geometrii
    positionAttribute.needsUpdate = true;

    // Inicjalizacja materiału podłoża
    this.groundMaterial = new THREE.MeshStandardMaterial({
      roughness: 0.8, // Zwiększona chropowatość dla bardziej naturalnego wyglądu
      metalness: 0.0, // Brak metaliczności
      color: new THREE.Color(0x33991a), // zielony kolor bazowy
      side: THREE.FrontSide,
      transparent: false,
      opacity: 1.0,
      envMapIntensity: 1.0, // Intensywność odbić środowiskowych
      //normalScale: new THREE.Vector2(1, 1), // Skala mapy normalnej
    });

    // Inicjalizacja materiału trawy z shaderami
    this.grassMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0.0 },
        grassColor: { value: new THREE.Color(0x2d8e1a) }, // Jaśniejszy kolor podstawy trawy
        grassTipColor: { value: new THREE.Color(0x7dda4e) }, // Jaśniejszy kolor czubka trawy
        playerPositions: {
          value: new Array(PLAYERS_COUNT).fill(new THREE.Vector3(0, -1000, 0)),
        }, // Domyślne pozycje graczy (poza mapą)
        playerCount: { value: 0 },
        playerRadius: { value: 4.0 }, // Zwiększony promień interakcji gracza z trawą
        fogColor: { value: new THREE.Color(0x87ceeb) },
        fogNear: { value: ENVIRONMENT_CONFIG.SKY.fogNear },
        fogFar: { value: ENVIRONMENT_CONFIG.SKY.fogFar },
      },
      vertexShader: this.vertexShader,
      fragmentShader: this.fragmentShader,
      side: THREE.FrontSide,
      transparent: false,
    });
  }

  /**
   * Ustawia kamerę do obliczania LOD
   * @param camera Kamera używana w scenie
   */
  public setCamera(camera: THREE.Camera): void {
    this.camera = camera;
  }

  /**
   * Oblicza poziom LOD na podstawie odległości od kamery
   * @param position Pozycja obszaru trawy
   * @returns Odpowiedni poziom LOD
   */
  private calculateLODLevel(position: THREE.Vector3): GrassLODLevel {
    if (!this.camera) return GRASS_LOD_LEVELS[this.currentQuality][0];

    const distanceSq = position.distanceToSquared(this.camera.position);
    const qualityLevels = GRASS_LOD_LEVELS[this.currentQuality];
    return qualityLevels.find((level) => distanceSq <= level.distance * level.distance) || qualityLevels[qualityLevels.length - 1];
  }

  /**
   * Tworzy lub aktualizuje obszar trawy na podstawie danych z serwera
   * @param grassElement Element środowiska reprezentujący obszar trawy
   */
  public createOrUpdateGrassPatch(grassElement: EnvironmentElement): void {
    const { id, position, scale } = grassElement;

    // Pobieramy aktualne ustawienia graficzne
    const graphicsSettings = GraphicsSettingsManager.getInstance().getCurrentSettings();

    // Sprawdzamy wartość grassDensity z ustawień - jeśli jest 0, nie tworzymy trawy
    if (graphicsSettings.grassDensity <= 0) {
      console.log("[GrassRenderer] Pomijanie tworzenia trawy - grassDensity:", graphicsSettings.grassDensity);

      // Usuwamy istniejący patch trawy, jeśli istnieje
      if (this.grassPatches.has(id)) {
        const existingPatch = this.grassPatches.get(id);
        if (existingPatch) {
          this.scene.remove(existingPatch);
        }
        this.grassPatches.delete(id);
      }

      // Usuwamy istniejące podłoże, jeśli istnieje (geometria współdzielona - tylko materiał)
      if (this.groundPatches.has(id)) {
        const existingGround = this.groundPatches.get(id);
        if (existingGround) {
          this.scene.remove(existingGround);
          if (Array.isArray(existingGround.material)) {
            existingGround.material.forEach((material) => material.dispose());
          } else {
            existingGround.material.dispose();
          }
        }
        this.groundPatches.delete(id);
      }

      this.grassPatchesData.delete(id);

      return;
    }

    // Zapisujemy dane o pozycji i skali obszaru trawy
    this.grassPatchesData.set(id, {
      position: new THREE.Vector3(position.x, position.y, position.z),
      scale: scale,
      isCulled: false,
    });

    // Usuwamy istniejące podłoże jeśli istnieje (geometria współdzielona - tylko materiał)
    if (this.groundPatches.has(id)) {
      const existingGround = this.groundPatches.get(id);
      if (existingGround) {
        this.scene.remove(existingGround);
        if (Array.isArray(existingGround.material)) {
          existingGround.material.forEach((material) => material.dispose());
        } else {
          existingGround.material.dispose();
        }
      }
      this.groundPatches.delete(id);
    }

    // Współdzielona geometria jednostkowa skalowana per patch - ten sam wygląd
    const groundGeo = GrassRenderer.getGroundGeometry();
    const groundMesh = new THREE.Mesh(groundGeo, this.groundMaterial.clone());
    groundMesh.scale.setScalar(scale);

    // Ustawiamy pozycję i rotację podłoża
    groundMesh.position.set(position.x, position.y + 0.01, position.z); // Lekko nad ziemią aby uniknąć z-fighting
    groundMesh.rotation.x = -Math.PI / 2;
    groundMesh.receiveShadow = true;

    // Dodaj subtelne zmiany koloru i właściwości dla każdego patcha trawy
    const groundMaterial = groundMesh.material as THREE.MeshStandardMaterial;
    const hue = 0.3 + (Math.random() * 0.1 - 0.05); // Losowy odcień zieleni
    const saturation = 0.6 + (Math.random() * 0.3 - 0.15); // Losowe nasycenie
    const lightness = 0.5 + (Math.random() * 0.2 - 0.1); // Losowa jasność
    groundMaterial.color.setHSL(hue, saturation, lightness);
    groundMaterial.roughness = 0.7 + Math.random() * 0.3; // Losowa chropowatość

    // Dodajemy podłoże do sceny i zapisujemy w mapie
    this.scene.add(groundMesh);
    this.groundPatches.set(id, groundMesh);

    // Tworzymy lub aktualizujemy obszar trawy z odpowiednim LOD
    this.updateGrassPatchLOD(id);
  }

  /**
   * Aktualizuje obszar trawy zgodnie z aktualnym poziomem LOD
   * @param id Identyfikator obszaru trawy
   */
  private updateGrassPatchLOD(id: string): void {
    const patchData = this.grassPatchesData.get(id);
    if (!patchData) return;

    // Pobieramy aktualny GraphicsSettingsManager, aby mieć pewność, że korzystamy z aktualnych ustawień
    const graphicsSettings = GraphicsSettingsManager.getInstance().getCurrentSettings();

    // Zapisujemy jakość do lokalnej zmiennej, aby używać jej spójnie w całej metodzie
    const qualitySettings = graphicsSettings.quality;

    // Upewniamy się, że używamy aktualnych wartości dla mnożników z poprawnego presetu grafiki
    const grassWidthMultiplier = graphicsSettings.grassWidthMultiplier;
    const grassHeightMultiplier = graphicsSettings.grassHeightMultiplier;

    // Obliczamy poziom LOD
    const lodLevel = this.calculateLODLevel(patchData.position);

    // Określamy liczbę źdźbeł trawy w obszarze - zależne od rozmiaru obszaru i poziomu LOD
    const baseGrassDensity = ENVIRONMENT_CONFIG.GRASS.BASE_DENSITY;
    const density = baseGrassDensity * lodLevel.density;
    const patchArea = Math.PI * patchData.scale * patchData.scale;
    const instanceCount = Math.floor(patchArea * density);

    // Jeśli gęstość jest zerowa lub liczba instancji jest zerowa, nie twórz wcale meshu trawy
    if (lodLevel.density <= 0 || instanceCount <= 0) {
      const existingPatch = this.grassPatches.get(id);
      if (existingPatch) {
        existingPatch.count = 0;
        existingPatch.visible = false;
      }
      return;
    }

    const grassInstancedMesh = this.getOrCreateGrassMesh(id, instanceCount);
    grassInstancedMesh.count = instanceCount;
    grassInstancedMesh.visible = true;

    this.populateGrassInstanceMatrices(
      grassInstancedMesh,
      instanceCount,
      patchData,
      grassWidthMultiplier,
      grassHeightMultiplier,
    );

    // Ustawiamy animację w zależności od poziomu LOD
    if (!lodLevel.animated) {
      // Dla dalszych poziomów LOD wyłączamy animację wiatru
      this.grassMaterial.uniforms.time.value = 0;
    }
  }

  private getOrCreateGrassMesh(id: string, requiredCount: number): THREE.InstancedMesh {
    const existingPatch = this.grassPatches.get(id);
    if (existingPatch) {
      const capacity = existingPatch.userData.maxCount ?? existingPatch.count;
      if (requiredCount <= capacity) {
        return existingPatch;
      }

      this.scene.remove(existingPatch);
      // Perf: release GPU instance buffers, otherwise every LOD regrow leaks.
      existingPatch.dispose();
      this.grassPatches.delete(id);
    }

    const capacity = requiredCount;
    const grassInstancedMesh = new THREE.InstancedMesh(this.grassGeometry, this.grassMaterial, capacity);
    grassInstancedMesh.name = id;
    // Perf: custom ShaderMaterial ignores scene lights/shadows, so shadow flags
    // only add a depth pass for thousands of blades with zero visual benefit.
    grassInstancedMesh.castShadow = false;
    grassInstancedMesh.receiveShadow = false;
    // Perf: InstancedMesh bounds cover only the base plane, not instances -
    // default culling would pop patches incorrectly. We cull manually by distance.
    grassInstancedMesh.frustumCulled = false;
    grassInstancedMesh.userData.maxCount = capacity;

    this.scene.add(grassInstancedMesh);
    this.grassPatches.set(id, grassInstancedMesh);
    return grassInstancedMesh;
  }

  private populateGrassInstanceMatrices(
    mesh: THREE.InstancedMesh,
    instanceCount: number,
    patchData: { position: THREE.Vector3; scale: number },
    grassWidthMultiplier: number,
    grassHeightMultiplier: number,
  ): void {
    const matrix = _grassMatrix;
    const rotationMatrixY = _grassRotationY;
    const patchRadius = patchData.scale;

    for (let i = 0; i < instanceCount; i++) {
      const radius = patchRadius * Math.sqrt(Math.random());
      const angle = Math.random() * Math.PI * 2;

      const x = patchData.position.x + radius * Math.cos(angle);
      const z = patchData.position.z + radius * Math.sin(angle);

      const rotationY = Math.random() * Math.PI * 2;
      const grassHeight = ENVIRONMENT_CONFIG.GRASS.GRASS_HEIGHT * grassHeightMultiplier + Math.random() * 0.6; // Używamy aktualnego mnożnika wysokości
      const grassWidth = ENVIRONMENT_CONFIG.GRASS.GRASS_WIDTH * grassWidthMultiplier + Math.random() * 0.1; // Używamy aktualnego mnożnika szerokości

      const rotationX = -Math.PI / 8 + (Math.random() * 0.2 - 0.1);

      matrix.makeRotationX(rotationX);
      rotationMatrixY.makeRotationY(rotationY);
      matrix.multiply(rotationMatrixY);
      matrix.setPosition(x, patchData.position.y + 0.3, z); // Raised slightly above ground level so track are below!
      this.instanceScale.set(grassWidth, grassHeight, 1);
      matrix.scale(this.instanceScale);

      mesh.setMatrixAt(i, matrix);
    }

    mesh.instanceMatrix.needsUpdate = true;
  }

  /**
   * Aktualizuje stan trawy (animacje, interakcje z graczami)
   */
  public update(): void {
    if (!this.camera) return;

    const currentTime = performance.now();
    const deltaTime = this.clock.getDelta();

    // Slow down the grass wind animation.
    this.grassMaterial.uniforms.time.value += deltaTime * 0.3;

    // Keep grass fog synced with the main scene fog.
    if (this.scene.fog instanceof THREE.Fog) {
      this.grassMaterial.uniforms.fogColor.value.copy(this.scene.fog.color);
      this.grassMaterial.uniforms.fogNear.value = this.scene.fog.near;
      this.grassMaterial.uniforms.fogFar.value = this.scene.fog.far;
    }

    // Aktualizacja liczby graczy
    this.grassMaterial.uniforms.playerCount.value = Math.min(this.playerPositions.size, PLAYERS_COUNT);

    if (currentTime - this.lastLODUpdateTime < this.LOD_UPDATE_INTERVAL) {
      return;
    }
    this.lastLODUpdateTime = currentTime;

    // LOD changes are relatively expensive, so check them at a fixed cadence.
    for (const [id, patchData] of this.grassPatchesData.entries()) {
      const patchDistanceSq = patchData.position.distanceToSquared(this.camera.position);
      const patch = this.grassPatches.get(id);

      // Hide very distant grass patches without rebuilding them every frame.
      if (patchDistanceSq > 130 * 130) {
        if (patch) {
          patch.visible = false;
          patch.count = 0;
        }
        patchData.isCulled = true;
        continue;
      }

      const currentLOD = this.calculateLODLevel(patchData.position);

      if (!patch) continue;

      const patchDataWithLOD = this.grassPatchesData.get(id);
      if (patchDataWithLOD && patchDataWithLOD["lastLODLevel"]) {
        const lastLODLevel = patchDataWithLOD["lastLODLevel"] as GrassLODLevel;

        if (
          patchDataWithLOD.isCulled ||
          lastLODLevel.distance !== currentLOD.distance ||
          Math.abs(lastLODLevel.density - currentLOD.density) > 0.1
        ) {
          this.updateGrassPatchLOD(id);
          patchDataWithLOD["lastLODLevel"] = currentLOD;
          patchDataWithLOD.isCulled = false;
        }
      } else {
        this.updateGrassPatchLOD(id);
        if (patchDataWithLOD) {
          patchDataWithLOD["lastLODLevel"] = currentLOD;
          patchDataWithLOD.isCulled = false;
        }
      }
    }
  }

  /**
   * Aktualizuje pozycję czołgu gracza do interakcji z trawą
   * @param playerId ID gracza
   * @param position Pozycja czołgu
   */
  public updatePlayerPosition(playerId: string, position: THREE.Vector3): void {
    this.playerPositions.set(playerId, position);

    // Aktualizacja uniform z pozycjami graczy bez alokacji
    this.playerPositionBuffer.forEach((vector, idx) => {
      vector.set(0, -1000, 0); // domyślne pozycje poza mapą
    });

    let index = 0;
    this.playerPositions.forEach((pos) => {
      if (index < PLAYERS_COUNT) {
        this.playerPositionBuffer[index].copy(pos);
        index++;
      }
    });

    this.grassMaterial.uniforms.playerPositions.value = this.playerPositionBuffer;
    this.grassMaterial.uniforms.playerCount.value = Math.min(this.playerPositions.size, PLAYERS_COUNT);
  }

  /**
   * Usuwa wszystkie obszary trawy ze sceny
   */
  public clearAllGrass(): void {
    this.grassPatches.forEach((mesh) => {
      this.scene.remove(mesh);
      // Dispose buforów instancji (współdzielona geometria bazowa zostaje)
      mesh.dispose();
    });
    this.grassPatches.clear();

    this.groundPatches.forEach((ground) => {
      this.scene.remove(ground);
      // Geometria współdzielona - dispose tylko klonowanego materiału
      if (Array.isArray(ground.material)) {
        ground.material.forEach((material) => material.dispose());
      } else {
        ground.material.dispose();
      }
    });
    this.groundPatches.clear();
    this.grassPatchesData.clear();
    this.playerPositions.clear();
  }

  /**
   * Usuwa konkretny obszar trawy
   * @param grassId ID obszaru trawy do usunięcia
   */
  public removeGrassPatch(id: string): void {
    // Usuwamy trawę (dispose buforów instancji, geometria współdzielona zostaje)
    if (this.grassPatches.has(id)) {
      const patch = this.grassPatches.get(id);
      if (patch) {
        this.scene.remove(patch);
        patch.dispose();
      }
      this.grassPatches.delete(id);
      this.grassPatchesData.delete(id);
    }

    // Usuwamy podłoże (geometria współdzielona - dispose tylko materiału)
    if (this.groundPatches.has(id)) {
      const ground = this.groundPatches.get(id);
      if (ground) {
        this.scene.remove(ground);
        if (Array.isArray(ground.material)) {
          ground.material.forEach((material) => material.dispose());
        } else {
          ground.material.dispose();
        }
      }
      this.groundPatches.delete(id);
    }
  }
}
